import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  LayoutDashboard, Bot, Settings, LogOut, Shield, X,
  Bell, Wrench, BookOpen, ShieldAlert, Wifi, WifiOff, Plug2, Cable,
  Globe, BarChart3, Zap, Radio, UsersRound, MonitorPlay, Menu, Inbox, CreditCard,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { usePortalSocketContext } from '@/contexts/PortalSocketContext';
import { SUPPORTED_LOCALES } from '@/i18n';
import styles from './AppShell.module.css';

type NavItem = {
  to: string;
  icon: typeof Bot;
  labelKey: string;
  countKey?: 'approvals' | 'handoffs' | 'alerts' | 'conversations';
};

type NavGroup = {
  groupKey: string;
  items: NavItem[];
};

const NAV_GROUPS: NavGroup[] = [
  {
    groupKey: 'overview',
    items: [
      { to: '/dashboard', icon: LayoutDashboard, labelKey: 'nav.dashboard' },
      { to: '/analytics', icon: BarChart3,       labelKey: 'nav.analytics' },
    ],
  },
  {
    groupKey: 'liveOps',
    items: [
      { to: '/inbox', icon: Inbox, labelKey: 'nav.inbox' },
    ],
  },
  {
    groupKey: 'aiPlatform',
    items: [
      { to: '/agents',         icon: Bot,         labelKey: 'nav.agents' },
      { to: '/channels',       icon: Cable,       labelKey: 'nav.channels' },
      { to: '/skills',         icon: Wrench,      labelKey: 'nav.skills' },
      { to: '/settings/tools', icon: Plug2,       labelKey: 'nav.tools' },
      { to: '/knowledge',      icon: BookOpen,    labelKey: 'nav.knowledge' },
      { to: '/guardrails',     icon: ShieldAlert, labelKey: 'nav.guardrails' },
    ],
  },
  {
    groupKey: 'automation',
    items: [
      { to: '/workflows', icon: Zap,   labelKey: 'nav.workflows' },
      { to: '/triggers',  icon: Radio, labelKey: 'nav.triggers' },
    ],
  },
  {
    groupKey: 'system',
    items: [
      { to: '/settings/members', icon: UsersRound,  labelKey: 'nav.members' },
      { to: '/teams',            icon: UsersRound,  labelKey: 'nav.teams' },
      { to: '/monitor',          icon: MonitorPlay, labelKey: 'nav.monitor' },
      { to: '/alerts',           icon: Bell,        labelKey: 'nav.alerts',   countKey: 'alerts' },
      { to: '/billing',          icon: CreditCard,  labelKey: 'nav.billing' },
      { to: '/settings',         icon: Settings,    labelKey: 'nav.settings' },
    ],
  },
];

/** Badge shown on nav items with live counts */
function NavBadge({ count, urgent }: { count: number; urgent?: boolean }) {
  if (count === 0) return null;
  return (
    <span style={{
      marginLeft: 'auto',
      minWidth: 18,
      height: 18,
      borderRadius: 9,
      background: urgent ? '#ef4444' : '#6366f1',
      color: '#fff',
      fontSize: 10,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 5px',
      lineHeight: 1,
      animation: count > 0 ? 'badge-pop 0.3s ease' : undefined,
    }}>
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function AppShell({ children }: { children: ReactNode }) {
  const { platformUser, loading, firebaseUser, isImpersonating, signOut, endImpersonation, setLanguage } = useAuth();
  const navigate = useNavigate();
  const { location } = useRouterState();
  const { counts, connected } = usePortalSocketContext();
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const closeSidebar = () => setSidebarOpen(false);

  // Redirect to login if not authenticated (skip for impersonation sessions)
  useEffect(() => {
    if (!loading && !firebaseUser && !isImpersonating) {
      navigate({ to: '/login' });
    }
  }, [loading, firebaseUser, isImpersonating, navigate]);

  if (loading) {
    return (
      <div className={styles.loadingScreen}>
        <div className={styles.loadingLogo}>e</div>
        <div className={styles.loadingSpinner} />
      </div>
    );
  }

  // During impersonation the /v1/me/impersonate call may still be in-flight
  if (!platformUser) {
    if (isImpersonating) {
      return (
        <div className={`${styles.loadingScreen} ${styles.loadingScreenSupport}`}>
          <div className={styles.loadingLogo}>e</div>
          <p className={styles.loadingTitle}>Starting support session</p>
          <p className={styles.loadingSubtitle}>Securely connecting to tenant workspace…</p>
          <div className={styles.loadingSpinner} />
          <div className={styles.loadingSteps}>
            <div className={styles.loadingStep}>
              <span className={styles.loadingStepDotDone} />
              Token validated
            </div>
            <div className={styles.loadingStep}>
              <span className={styles.loadingStepDot} />
              Loading tenant context…
            </div>
            <div className={styles.loadingStep}>
              <span className={styles.loadingStepDot} />
              Preparing workspace
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  const initials = (platformUser.name ?? platformUser.email)
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const currentLocale = SUPPORTED_LOCALES.find(l => l.code === i18n.language) ?? SUPPORTED_LOCALES[0];

  return (
    <div className={styles.shell}>
      {/* ── Support session banner ── */}
      {isImpersonating && (
        <div className={styles.supportBanner}>
          <Shield size={14} />
          <span>
            <strong>Support session</strong> — viewing as {platformUser.email} in{' '}
            <strong>{platformUser.tenant?.name}</strong>. This tab closes when you end the session.
          </span>
          <button className={styles.endSessionBtn} onClick={endImpersonation} title="End support session">
            <X size={14} /> End session
          </button>
        </div>
      )}

      {/* ── Sidebar + Main ── */}
      <div className={styles.innerWrap}>
        {/* ── Hamburger (mobile) ── */}
        <button
          className={styles.hamburger}
          onClick={() => setSidebarOpen(o => !o)}
          aria-label="Toggle navigation"
        >
          {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
        </button>

        {/* ── Overlay (mobile) ── */}
        {sidebarOpen && <div className={styles.overlay} onClick={closeSidebar} />}

        {/* ── Sidebar ── */}
        <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarOpen : ''}`}>
          <div className={styles.sidebarTop}>
            {/* Logo + WS status */}
            <div className={styles.logo}>
              <div className={styles.logoMark}>e</div>
              <span className={styles.logoText}>enska.ai</span>
              <span
                title={connected ? 'Live' : 'Reconnecting…'}
                style={{ marginLeft: 'auto', color: connected ? '#10b981' : '#ef4444', opacity: 0.7 }}
              >
                {connected ? <Wifi size={12} /> : <WifiOff size={12} />}
              </span>
            </div>

            {/* Tenant badge */}
            <div className={styles.tenantBadge}>
              <span className={styles.tenantDot} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className={styles.tenantName}>{platformUser.tenant?.name}</div>
                <div style={{ marginTop: 2 }}>
                  <span className={styles.tenantPlan}>{platformUser.tenant?.plan}</span>
                </div>
              </div>
            </div>

            {/* Grouped nav */}
            <nav className={styles.nav} aria-label="Main navigation">
              {NAV_GROUPS.map((group, gi) => (
                <div key={group.groupKey}>
                  {/* Group label (skip first group — Dashboard stands alone) */}
                  <div className={styles.navGroup} style={{ marginTop: gi === 0 ? 0 : 8 }}>
                    <span className={styles.navGroupLabel}>{t(`nav.groups.${group.groupKey}`)}</span>
                  </div>

                  {group.items.map(({ to, icon: Icon, labelKey, countKey }) => {
                    // /settings/tools is a top-level nav item — prevent /settings from also lighting up
                    const isSettingsItem = to === '/settings';
                    const active = isSettingsItem
                      ? (location.pathname === to || location.pathname.startsWith(`${to}/`)) && !location.pathname.startsWith('/settings/tools')
                      : location.pathname === to || location.pathname.startsWith(`${to}/`);
                    const count = countKey ? counts[countKey] : 0;
                    const urgent = countKey === 'approvals' || countKey === 'alerts';
                    return (
                      <Link
                        key={to}
                        to={to}
                        onClick={closeSidebar}
                        className={`${styles.navItem} ${active ? styles.navItemActive : ''}`}
                        aria-current={active ? 'page' : undefined}
                      >
                        <Icon size={17} />
                        <span>{t(labelKey)}</span>
                        <NavBadge count={count} urgent={urgent} />
                      </Link>
                    );
                  })}
                </div>
              ))}
            </nav>
          </div>

          {/* User footer */}
          <div className={styles.userSection}>

            {/* User card — avatar + name + role */}
            <div className={styles.userCard}>
              <div className={`${styles.avatar} ${isImpersonating ? styles.avatarSupport : ''}`} aria-hidden="true">
                {isImpersonating ? <Shield size={14} /> : initials}
              </div>
              <div className={styles.userInfo}>
                <p className={styles.userName}>{platformUser.name ?? platformUser.email}</p>
                <span className={styles.userRole}>{isImpersonating ? 'Support' : platformUser.role}</span>
              </div>
            </div>

            {/* Action row — language + sign out */}
            <div className={styles.userActions}>
              {/* Language switcher */}
              <div style={{ position: 'relative', flex: 1 }}>
                <button
                  className={styles.langBtn}
                  onClick={() => setLangOpen(o => !o)}
                  aria-label={t('language.label')}
                >
                  <Globe size={11} />
                  <span>{currentLocale.flag} {currentLocale.label}</span>
                </button>

                {langOpen && (
                  <div style={{
                    position: 'absolute', bottom: '100%', left: 0, right: 0, marginBottom: 4,
                    background: 'var(--surface-2)', border: '1px solid var(--border)',
                    borderRadius: 8, overflow: 'hidden', boxShadow: '0 -8px 24px rgba(0,0,0,0.4)',
                    zIndex: 200,
                  }}>
                    {SUPPORTED_LOCALES.map(locale => (
                      <button
                        key={locale.code}
                        onClick={async () => { await setLanguage(locale.code); setLangOpen(false); }}
                        style={{
                          width: '100%', padding: '8px 12px', background: 'none', border: 'none',
                          color: i18n.language === locale.code ? '#818cf8' : 'var(--color-text)',
                          cursor: 'pointer', fontSize: 12, textAlign: 'left',
                          display: 'flex', alignItems: 'center', gap: 8,
                          fontWeight: i18n.language === locale.code ? 700 : 400,
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#ffffff08'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                      >
                        <span>{locale.flag}</span>
                        <span>{t(`language.${locale.code}`)}</span>
                        {i18n.language === locale.code && (
                          <span style={{ marginLeft: 'auto', fontSize: 10, color: '#6366f1' }}>✓</span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                className={styles.signOutBtn}
                onClick={isImpersonating ? endImpersonation : signOut}
                title={isImpersonating ? 'End support session' : 'Sign out'}
                aria-label={isImpersonating ? 'End support session' : 'Sign out'}
              >
                <LogOut size={13} />
                <span>{isImpersonating ? 'End' : 'Sign out'}</span>
              </button>
            </div>

          </div>

        </aside>

        {/* ── Main ── */}
        <main className={`${styles.main} ${isImpersonating ? styles.mainSupport : ''}`}>
          {children}
        </main>
      </div>

      <style>{`
        @keyframes badge-pop {
          0%   { transform: scale(0.6); }
          60%  { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
